import { ApiBase } from '@/api/methods/ApiBase'
import {
  ApiResponseStudent,
  ApiResponseStudents,
  ApiResponseStudentProgresses,
} from '@/api/responses/manage/userStudents'

export namespace UserStudentsApi {
  /**
   * オプション引数(非必須のパラメータ)の型
   */
  type OptionParams = {
    /** 学年コード */
    gradeCode?: string
    /** 教室ID */
    branchId?: number
    /** コースID */
    courseId?: number
    /** クラスID */
    classId?: number
    /** 検索文字列 */
    searchText?: string
  }

  /**
   * 生徒一覧取得API
   *
   * @param pageNumber ページ番号
   * @param count 表示件数
   * @param options 非必須の絞り込み条件(gradeCode, branchId, courseId, classId, searchText)
   * @returns 生徒一覧
   */
  export async function getStudents(
    pageNumber: number,
    count: number,
    options: OptionParams
  ): Promise<ApiResponseStudents> {
    const params = {
      pageNumber: pageNumber,
      count: count,
    }
    // 学年コードが存在すれば追加
    if (options.gradeCode) {
      Object.assign(params, { gradeCode: options.gradeCode })
    }
    // 教室IDが存在すれば追加
    if (options.branchId) {
      Object.assign(params, { branchId: options.branchId })
    }
    // コースIDが追加すれば追加
    if (options.courseId) {
      Object.assign(params, { courseId: options.courseId })
    }
    // クラスIDが存在すれば追加
    if (options.classId) {
      Object.assign(params, { classId: options.classId })
    }
    // 検索文字列があれば追加
    if (options.searchText) {
      Object.assign(params, { searchText: options.searchText })
    }
    return await ApiBase.get('manage/users/students', params)
  }

  /**
   * 生徒単体取得API
   *
   * @param userId ユーザID
   * @returns 生徒情報
   */
  export async function getStudent(userId: number): Promise<ApiResponseStudent> {
    return await ApiBase.get(`manage/users/students/${userId}`)
  }

  /**
   * 生徒毎通常学習進捗一覧取得API
   *
   * @param studyCategoryId 教材ID
   * @param pageNumber ページ番号
   * @param count 表示件数
   * @param options 非必須の絞り込み条件(gradeCode, branchId, courseId, classId, searchText)
   * @returns 生徒一覧
   */
  export async function getStudentGeneralLearningProgresses(
    studyCategoryId: number,
    pageNumber: number,
    count: number,
    options: OptionParams
  ): Promise<ApiResponseStudentProgresses> {
    const params = {
      studyCategoryId: studyCategoryId,
      pageNumber: pageNumber,
      count: count,
    }
    // 学年コードが存在すれば追加
    if (options.gradeCode) {
      Object.assign(params, { gradeCode: options.gradeCode })
    }
    // 教室IDが存在すれば追加
    if (options.branchId) {
      Object.assign(params, { branchId: options.branchId })
    }
    // コースIDが追加すれば追加
    if (options.courseId) {
      Object.assign(params, { courseId: options.courseId })
    }
    // クラスIDが存在すれば追加
    if (options.classId) {
      Object.assign(params, { classId: options.classId })
    }
    // 検索文字列があれば追加
    if (options.searchText) {
      Object.assign(params, { searchText: options.searchText })
    }
    return await ApiBase.get('manage/users/students/progress/general', params)
  }

  /**
   * 生徒毎知識MAP系統図進捗一覧取得API
   *
   * @param systemChartId 系統図ID
   * @param pageNumber ページ番号
   * @param count 表示件数
   * @param options 非必須の絞り込み条件(gradeCode, branchId, courseId, classId, searchText)
   * @returns 生徒一覧
   */
  export async function getStudentSystemChartProgresses(
    systemChartId: number,
    pageNumber: number,
    count: number,
    options: OptionParams
  ): Promise<ApiResponseStudentProgresses> {
    const params = {
      systemChartId: systemChartId,
      pageNumber: pageNumber,
      count: count,
    }
    // 学年コードが存在すれば追加
    if (options.gradeCode) {
      Object.assign(params, { gradeCode: options.gradeCode })
    }
    // 教室IDが存在すれば追加
    if (options.branchId) {
      Object.assign(params, { branchId: options.branchId })
    }
    // コースIDが追加すれば追加
    if (options.courseId) {
      Object.assign(params, { courseId: options.courseId })
    }
    // クラスIDが存在すれば追加
    if (options.classId) {
      Object.assign(params, { classId: options.classId })
    }
    // 検索文字列があれば追加
    if (options.searchText) {
      Object.assign(params, { searchText: options.searchText })
    }
    return await ApiBase.get('manage/users/students/progress/map', params)
  }

  /**
   * 生徒情報更新API
   *
   * @param userId ユーザID
   * @param nickname ニックネーム
   * @param identifyTag 生徒識別子
   * @param gradeCode 学年コード
   * @param courseId コースID
   * @param classId クラスID
   */
  export async function updateStudent(
    userId: number,
    nickname: string,
    identifyTag: string,
    gradeCode: string,
    courseId: number,
    classId: number
  ): Promise<void> {
    const params = {
      nickname: nickname,
      identifyTag: identifyTag,
      gradeCode: gradeCode,
      courseId: courseId,
      classId: classId,
    }
    await ApiBase.post(`manage/users/students/${userId}/update`, params)
  }
}
